import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useUserStore } from './userStore';
import { useActivityLogStore } from './activityLogStore';
import { calculateTierInfo } from '../utils/levelCalculation';
import { accountStorage, scopeToAccount } from '../lib/accountStorage';

export type LevelUpContext = 'immediate' | 'deferred';

export interface DomainLevelUpData {
  type: 'domain';
  categoryId: string;
  categoryName: string;
  categoryColor: string;
  categoryIcon: string;
  oldLevel: number;
  newLevel: number;
  oldTotalLevel: number;
  newTotalLevel: number;
  newPoints: number;
}

export interface UserLevelUpData {
  type: 'user';
  oldLevel: number;
  newLevel: number;
  oldTotalLevel: number;
  newTotalLevel: number;
  newPoints: number;
}

export type LevelUpData = DomainLevelUpData | UserLevelUpData;

export interface LevelUpQueueItem {
  id: string;
  data: LevelUpData;
  context: LevelUpContext;
  timestamp: number;
}

interface LevelUpState {
  queue: LevelUpQueueItem[];
  celebratedIds: string[];
  enqueueLevelUp: (data: LevelUpData, context: LevelUpContext) => void;
  dequeueNext: () => LevelUpQueueItem | null;
  markCelebrated: (id: string) => void;
  hasBeenCelebrated: (id: string) => boolean;
  clearCelebrated: () => void;
}

export const useLevelUpStore = create<LevelUpState>()(
  persist(
    (set, get) => ({
      queue: [],
      celebratedIds: [],
      enqueueLevelUp: (data, context) => {
        const id = generateLevelUpId(data);

        if (get().hasBeenCelebrated(id)) {
          return;
        }

        /**
         * ⭐⭐ THE ONE PLACE A LEVEL CROSSING IS KNOWN AS IT HAPPENS — so it is
         * where the date gets stamped. The Achievements title ladder reads
         * `levelReachedAt` to say "Earned <date>"; deriving it later from the
         * activity log is not possible (that log is sparse — one row on a real
         * Level 8 account) and reading it off a page visit would record the day
         * you next opened Achievements, not the day you earned the title.
         *
         * ⚠️ Stamps EVERY level in the span. A single award can cross more than
         * one, and `oldLevel + 1` alone would leave the rest permanently blank.
         * ⚠️ Sits above the celebration queue on purpose: the record is owed
         * whether or not the user ever clicks through the overlay.
         */
        const scope = data.type === 'domain' ? data.categoryId : 'user';
        /**
         * ⚠️⚠️ THE SPAN IS WALKED IN TOTAL LEVELS, THEN MAPPED TO LADDER RUNGS —
         * walking the tier-relative `oldLevel + 1 … newLevel` instead stamped
         * NOTHING AT ALL whenever the span crossed a tier boundary, because
         * those two wrap independently. `100 → 101` reads as `100 → 1`, so the
         * loop never runs; `98 → 102` reads as `98 → 2` and loses rungs 99 and
         * 100 as well, which are nowhere near the boundary. Silent: the titles
         * simply keep saying "Reached at level N" and nothing looks broken.
         * Total levels are monotonic, so the span is always walkable.
         *
         * ⚠️ The rung comes from `calculateTierInfo`, NOT a local `% 100` — it
         * is the same function behind the `level` TitleLadder compares against,
         * including its tier-end convention (total 100 is rung 100, not rung 0),
         * and `levelsPerTier` is re-hydratable from the economy config. A
         * hand-rolled modulo here would drift from the ladder the day either
         * changes, and the drift would only show as missing dates.
         */
        const crossed: number[] = [];
        for (let t = data.oldTotalLevel + 1; t <= data.newTotalLevel; t++) {
          crossed.push(calculateTierInfo(t).tierLevel);
        }
        /**
         * ⚠️⚠️ A SPAN THAT STARTS AT ZERO IS NOT A LEVEL-UP — it is the app
         * catching up with itself, and dating it writes TODAY onto levels earned
         * months ago.
         *
         * `useLevelUpDetection` takes its baseline on first mount from whatever
         * `stats` says at that instant. On a device or origin with no persisted
         * baseline the stores have not hydrated yet, so that snapshot is all
         * zeros; the next pass then reads `0 → 13` and enqueues a "level up" for
         * ground covered long ago. Live-proved on the real account: this stamped
         * ecological:1, personal:13, personal:16 and existential:10 all with
         * today's date.
         *
         * ⚠️ THE CELEBRATION IS LEFT ALONE — only the DATE is suppressed.
         * Changing what raises an overlay is a separate decision, and a spurious
         * overlay is visible and dismissable where a false date is silent and
         * permanent.
         *
         * ⚠️ Cost, accepted: a brand-new account's very FIRST level in a scope
         * goes undated, since a genuine 0→1 and a hydration 0→1 are
         * indistinguishable from here. That card falls back to "Reached at level
         * N", which is true. Every level after it is dated.
         */
        const baselineTrusted = data.oldTotalLevel > 0;
        if (baselineTrusted && crossed.length > 0) {
          // ⚠️ `getState()` at call time, never a captured reference — this
          // store hydrates asynchronously and a render-time closure would read
          // a stale identity blob and drop earlier stamps.
          useUserStore.getState().recordLevelReached(scope, crossed);
        }

        /**
         * ⭐⭐ THE JOURNAL ENTRY FOR THE CROSSING — the missing writer.
         * `activityType: 'level_up'` has been declared, rendered (ActivityItem)
         * and filterable (ProgressLogTab's "Level Ups") since the day it
         * shipped, but NOTHING EVER WROTE ONE, so that filter returned an empty
         * list forever and the render branch was unreachable. Same phantom-value
         * class as `programStatus === 'completed'`.
         *
         * ⚠️⚠️ `xpEarned: 0` IS LOAD-BEARING, not a placeholder. A level-up is a
         * CONSEQUENCE of XP, never a source of it, and `getDailyXPTotals` sums
         * this field straight into the XP chart — any other value double-counts
         * the very XP that caused the crossing, on the chart and in every
         * "best single day" stat derived from it.
         *
         * ⚠️ TOTAL levels here, not the tier-relative `oldLevel`/`newLevel` used
         * for the stamp above. The rest of the app says "Level 13" meaning the
         * total, and the total never wraps at a tier boundary.
         *
         * ⚠️ `domain_id` is NOT NULL (migration 0025), so the user scope writes
         * `''` rather than null. No category matches it — which is exactly what
         * makes ActivityItem fall back to the level-up icon and keeps the entry
         * out of ProgressLogTab's per-domain filter.
         */
        const alreadyLogged = useActivityLogStore
          .getState()
          .logs.some(l => l.metadata?.levelUpId === id);
        if (!alreadyLogged) {
          void useActivityLogStore.getState().addActivity({
            activityType: 'level_up',
            activityName: data.type === 'domain'
              ? `${data.categoryName} Level ${data.newTotalLevel}`
              : `Level ${data.newTotalLevel}`,
            domainId: data.type === 'domain' ? data.categoryId : '',
            xpEarned: 0,
            metadata: {
              levelType: data.type,
              oldLevel: data.oldTotalLevel,
              newLevel: data.newTotalLevel,
              /**
               * ⚠️ THE DURABLE IDEMPOTENCY KEY. `celebratedIds` cannot serve as
               * one — it self-clears after 60s — and `levelReachedAt` is keyed
               * tier-relative, so it cannot answer "did I already log THIS
               * crossing" across a reload or a second device. The log is its own
               * dedup ledger, and this id is stable per crossing.
               */
              levelUpId: id,
            },
            completedAt: new Date().toISOString(),
          });
        }

        const queueItem: LevelUpQueueItem = {
          id,
          data,
          context,
          timestamp: Date.now()
        };

        set((state) => ({
          queue: [...state.queue, queueItem]
        }));
      },

      dequeueNext: () => {
        const state = get();
        const immediateItems = state.queue.filter(item => item.context === 'immediate');

        if (immediateItems.length === 0) {
          return null;
        }

        const nextItem = immediateItems[0];

        set((state) => ({
          queue: state.queue.filter(item => item.id !== nextItem.id)
        }));

        return nextItem;
      },

      markCelebrated: (id) => {
        set((state) => ({
          celebratedIds: [...state.celebratedIds, id]
        }));

        setTimeout(() => {
          set((state) => ({
            celebratedIds: state.celebratedIds.filter(cid => cid !== id)
          }));
        }, 60000);
      },

      hasBeenCelebrated: (id) => {
        return get().celebratedIds.includes(id);
      },

      clearCelebrated: () => {
        set({ celebratedIds: [] });
      },



    }),
    {
      name: 'level-up-storage',
      // ⚠️ `previousLevels` USED TO BE PERSISTED HERE and is deliberately gone.
      // A per-DEVICE baseline is the bug: a device that had never seen the
      // account defaulted every scope to 0 and read the first real snapshot as a
      // level-up. The baseline now lives in `userStore.levelBaseline`, which
      // rides the cloud identity blob and therefore arrives with the account.
      // A stale key left in localStorage is simply ignored by zustand.
      partialize: (state) => ({
        celebratedIds: state.celebratedIds,
      } as any),
      /* ⚠️ Per account (lib/accountStorage): every change of account starts the queue over,
         so a level-up still waiting for the account that left never opens for the next. */
      storage: accountStorage('claim'),
    }
  )
);

scopeToAccount(useLevelUpStore);

function generateLevelUpId(data: LevelUpData): string {
  if (data.type === 'domain') {
    return `domain-${data.categoryId}-${data.newTotalLevel}`;
  } else {
    return `user-${data.newTotalLevel}`;
  }
}
